"use client";

import { c as _c } from "react/compiler-runtime";
import React, { createContext, useContext, useState, useMemo } from "react";
import { useAllSportsLivescoreMatches } from "@/views/livescore/hooks/useAllSportsLivescoreMatches";
import { useServerOddsByDate } from "@/features/odds/useServerOddsByDate";
import { toMatchOddsMap } from "@/features/odds/adapters";
import type { MatchOddsMap } from "@/features/odds/types";
import type { LivescoreBoard } from "@/views/livescore/utils/livescore";
import { todayISODate } from "@/utilities/formatting/date";
import { useTvListingsByDate } from "@/views/livescore/hooks/useTvListingsByDate";
import type { ResolvedListings } from "@/features/tv-listings/utils/resolveMatchListings";
import type { LocaleCode } from "@/lib/labels/types";
import type { LivescoreBoardSeed } from "@/features/live-data/livescoreParams";
interface HomeLivescoreContextValue extends LivescoreBoard {
  loading: boolean;
  /** Batched client-side odds map, keyed by football event id. Basketball / tennis have no odds source right now. */
  oddsMap: MatchOddsMap;
  oddsLoading: boolean;
  /** Pre-resolved TV broadcaster listings for the day, keyed by native match ID. */
  tvByMatch: Record<string, ResolvedListings>;
  /** True when any match on this day has broadcasters — drives whether rows reserve the TV slot. */
  anyTvForDay: boolean;
}
const HomeLivescoreContext = createContext<HomeLivescoreContextValue | null>(null);
interface HomeLivescoreProviderProps {
  locale: LocaleCode;
  /** Server-rendered board for today; skips the mount fetch when it matches. */
  initialBoard?: LivescoreBoardSeed;
  children: React.ReactNode;
}

/**
 * Fetches the livescore board once for every homepage consumer (ticker + widget)
 * and layers batched football odds on top. Both consumers read from the same
 * context so we make one livescore request and one batched-odds request per
 * homepage render, not one pair per component.
 */
export function HomeLivescoreProvider(t0) {
  const $ = _c(21);
  const {
    locale,
    initialBoard,
    children
  } = t0;
  const [date] = useState(todayISODate);
  let t1;
  if ($[0] !== date || $[1] !== initialBoard || $[2] !== locale) {
    t1 = {
      date,
      locale,
      initialBoard
    };
    $[0] = date;
    $[1] = initialBoard;
    $[2] = locale;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const board = useAllSportsLivescoreMatches(t1);
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["1x2"];
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== date || $[6] !== locale) {
    t3 = {
      date,
      sport: "football",
      markets: t2,
      locale
    };
    $[5] = date;
    $[6] = locale;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const {
    oddsMap: rawOddsMap,
    loading: oddsLoading
  } = useServerOddsByDate(t3);
  let t4;
  if ($[8] !== rawOddsMap) {
    t4 = toMatchOddsMap(rawOddsMap);
    $[8] = rawOddsMap;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const oddsMap = t4;
  const {
    tvByMatch
  } = useTvListingsByDate(date, locale);
  let t5;
  if ($[10] !== tvByMatch) {
    t5 = Object.keys(tvByMatch);
    $[10] = tvByMatch;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const anyTvForDay = t5.length > 0;
  let t6;
  if ($[12] !== anyTvForDay || $[13] !== board || $[14] !== oddsLoading || $[15] !== oddsMap || $[16] !== tvByMatch) {
    t6 = {
      ...board,
      oddsMap,
      oddsLoading,
      tvByMatch,
      anyTvForDay
    };
    $[12] = anyTvForDay;
    $[13] = board;
    $[14] = oddsLoading;
    $[15] = oddsMap;
    $[16] = tvByMatch;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const value = t6;
  let t7;
  if ($[18] !== children || $[19] !== value) {
    t7 = <HomeLivescoreContext.Provider value={value}>{children}</HomeLivescoreContext.Provider>;
    $[18] = children;
    $[19] = value;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
}
export function useHomeLivescoreData() {
  const ctx = useContext(HomeLivescoreContext);
  if (!ctx) {
    throw new Error("useHomeLivescoreData must be used within HomeLivescoreProvider");
  }
  return ctx;
}